import { Room } from "@/types/room";
import { UserProfile } from "@/types/user-profile";

export interface ProfileHotelAccess {
  hotelId: string;
  hotelName: string;
  isEnabled?: boolean;
  isActive?: boolean;
}

export enum PmsType {
  MEWS = "MEWS",
  OPERA = "OPERA",
}

export enum WifiSecurity {
  WPA = "WPA",
  WEP = "WEP",
  NONE = "NONE",
}

export const WIFI_SECURITY_OPTIONS: {
  value: WifiSecurity;
  labelKey: string;
}[] = [
  { value: WifiSecurity.WPA, labelKey: "hotels.form.wifiSecurityWpa" },
  { value: WifiSecurity.WEP, labelKey: "hotels.form.wifiSecurityWep" },
  { value: WifiSecurity.NONE, labelKey: "hotels.form.wifiSecurityNone" },
];

export interface ChatSettings {
  id?: string;
  hotelId: string;
  isAiEnabled: boolean;
  isTranslationEnabled: boolean;
  defaultLanguage: string;
  textModel: string;
  transcriptionModel: string;
  routerTemperature: number;
  defaultTemperature: number;
  translationTemperature: number;
  recursionLimit: number;
  confidenceThreshold: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface UpdateChatSettingsDto {
  isAiEnabled?: boolean;
  isTranslationEnabled?: boolean;
  defaultLanguage?: string;
  textModel?: string | null;
  transcriptionModel?: string | null;
  routerTemperature?: number | null;
  defaultTemperature?: number | null;
  translationTemperature?: number | null;
  recursionLimit?: number | null;
  confidenceThreshold?: number | null;
}

export interface HotelAddress {
  street: string;
  city: string;
  state?: string;
  country: string;
  postalCode?: string;
  fullAddress: string;
  googlePlaceId?: string | null;
  googleMapsCid?: string | null;
}

export interface HotelPaymentSettings {
  paymentEnabled: boolean;
  currency: string;
}

export interface Hotel {
  id: string;
  name: string;
  currency?: string;
  logoId?: string;
  hotelAddress: HotelAddress;
  wifiNetwork: string | null;
  wifiPassword: string | null;
  wifiSecurity?: WifiSecurity;
  tenantId: string;
  employees?: UserProfile[];
  rooms: Room[];
  pms: PmsType;
  isEnabled?: boolean;
  isActive?: boolean;
  createdAt: Date;
  updatedAt: Date;

  mapFileId?: string;
  policyText?: string;
  description?: string | null;
  timezone?: string | null;
  website?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  socialMediaInstagram?: string | null;
  socialMediaFacebook?: string | null;
  socialMediaOther?: string | null;
  chatSettings?: ChatSettings;
  paymentSettings?: HotelPaymentSettings;
}

export interface CreateHotelDto {
  name: string;
  logoId?: string;
  mapFileId?: string;
  policyText?: string;
  description?: string | null;
  timezone?: string | null;
  website?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  socialMediaInstagram?: string | null;
  socialMediaFacebook?: string | null;
  socialMediaOther?: string | null;
  address: HotelAddress;
  pms: PmsType;
  wifiNetwork?: string;
  wifiPassword?: string;
  wifiSecurity?: WifiSecurity;
  managerIds: string[];
  chatSettings?: UpdateChatSettingsDto;
}

export interface UpdateHotelDto {
  name?: string;
  logoId?: string;
  mapFileId?: string;
  policyText?: string;
  description?: string | null;
  timezone?: string | null;
  website?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  socialMediaInstagram?: string | null;
  socialMediaFacebook?: string | null;
  socialMediaOther?: string | null;
  address?: HotelAddress;
  wifiNetwork?: string;
  wifiPassword?: string;
  wifiSecurity?: WifiSecurity;
  managerIds?: string[];
  pms?: PmsType;
  isEnabled?: boolean;
  chatSettings?: UpdateChatSettingsDto;
}

export interface HotelFilters {
  sortField?: string | null;
  sortDirection?: "asc" | "desc" | null;
  currentPage?: number;
  perPage?: number;
  tenantId?: string;
  ids?: string[];
  search?: string;
  status?: "active" | "inactive";
  pms?: PmsType;
  rooms?: number;
  roomsMin?: number;
  roomsMax?: number;
  employeesMin?: number;
  employeesMax?: number;
  billingPlanId?: string;
}

export interface IPaginationResponse<T> {
  total: number;
  data: T[];
}

export interface HotelListSummary {
  total: number;
  active: number;
  inactive: number;
  totalRooms: number;
}

export interface HotelsListResponse extends IPaginationResponse<Hotel> {
  summary?: HotelListSummary;
}
